import * as THREE from '@phdsoft/three';

/** Everything the renderer needs to decide whether to run the ambient occlusion pass this frame. */
export interface AmbientOcclusionGateState {
  enabled: boolean;
  // was: /** Asset.viewMode */ viewMode: string | null | undefined; — AO ran in the default view mode only
  /** Asset.viewMode; only the FEA view modes (FEA_VIEW_MODES) turn AO off. */
  viewMode: string | null | undefined;
  /**
   * The structure is drawn see-through: GraphicsMaterials.basicMaterial has opacity < 1 (X-ray
   * sensor view 0.25, box-measurement tool 0.5) or depthWrite off (Transparence view). Depth-based
   * AO over a see-through model shades what is behind it, so it is skipped; every opaque view
   * mode (thickness, risk, ids, labels...) keeps it.
   */
  structureTransparent: boolean;
  /** Asset.mode: 'MODEL' | 'REGIONS' | 'SECTIONS'; no longer gates AO, kept for ambientOcclusionWorldRadius. */
  navigationMode: string | null | undefined;
  /** Asset.isLightModel; no longer gates AO, kept for ambientOcclusionWorldRadius. */
  isLightModel: boolean;
  /** Graphics.isDragMode */
  isDragMode: boolean;
  cameraMoving: boolean;
}

// was: /** An unset view mode is the default view mode. */
// was: function isDefaultViewMode(viewMode: string | null | undefined): boolean {
// was:   return !viewMode || viewMode === 'DEFAULT';
// was: }

/**
 * The FEA view modes (Asset.viewMode set by AssetManagerFea.enterFEA). Their contour colours are
 * result values read against the legend: AO darkening corners and seams would read as different
 * values, so AO is off in all of them (Show Mesh is a toggle inside FEA Results).
 */
export const FEA_VIEW_MODES: readonly string[] = ['FEAResults', 'PlatePanels', 'StiffenedPanels', 'LargePanels'];

export function isFeaViewMode(viewMode: string | null | undefined): boolean {
  return !!viewMode && FEA_VIEW_MODES.includes(viewMode.split('|')[0]);
}

/**
 * Ambient occlusion runs on a still camera whenever the structure is drawn opaque: every view
 * mode that recolours the model (thickness, risk, ids, labels...) keeps it; the modes and
 * tools that make the model see-through (Transparence, X-ray sensor, box measurement) skip it,
 * because depth-based AO would shade through them. The FEA view modes skip it too (see
 * FEA_VIEW_MODES). The light model in MODEL navigation keeps it, with a world radius scaled
 * by the camera distance (see ambientOcclusionWorldRadius).
 * was: default view mode only; then also off for the light model in MODEL navigation.
 */
export function shouldApplyAmbientOcclusion(state: AmbientOcclusionGateState): boolean {
  return state.enabled
    && !state.isDragMode
    && !state.cameraMoving
    && !state.structureTransparent
    && !isFeaViewMode(state.viewMode);
  // was: && !(state.navigationMode === 'MODEL' && state.isLightModel); — the fixed 1 m radius was ~1 px at whole-ship view; the radius now scales with camera distance there
}

/**
 * How long (ms) the camera must stay still after its last detected motion before
 * AO comes back. Without it, AO flashed on for one frame between wheel notches and
 * between the pointer events of a slow drag (pointer events arrive slower than
 * frames on trackpads and high-refresh displays).
 */
export const AO_SETTLE_MS = 180;

/**
 * How long (ms) the AO blend intensity takes to ramp from 1 to 0 once camera motion is
 * detected. The pass re-blends the AO of the last still camera on those frames (computing it
 * costs up to ~90 ms per frame in cluttered interior views), so the fading shadows drift a few
 * pixels while they go. With AO_FADE_IN_MS the only tuning knob.
 */
// was: export const AO_FADE_OUT_MS = 100; — two visible frames at the ~20 fps the app runs at while moving: read as a pop (UAT 2026-10-27)
export const AO_FADE_OUT_MS = 150;

/**
 * How long (ms) the AO blend intensity takes to ramp from 0 to 1 after the settle window
 * has ended. Ramps advance by elapsed time, so both are frame-rate independent.
 */
// was: export const AO_FADE_IN_MS = 200; — six frames at ~20 fps, barely noticed (UAT 2026-10-27); the fade-in is cheap (cached AO) and the camera is still, so it can be long
export const AO_FADE_IN_MS = 400;

/**
 * Largest elapsed time (ms) one frame may advance a ramp by. The idle render gate renders
 * nothing while the camera rests, so the first motion frame can arrive hundreds of ms after
 * the last decision; uncapped, that single frame would complete the fade-out and pop the AO
 * off exactly as before (the same for a fade-in after a view-mode switch). One 20 fps frame:
 * a fade-out always takes at least 3 frames and a fade-in at least 8, at any frame rate at or
 * above 20 fps (what the app reaches while moving at pixel ratio 2) the ramps stay time-exact,
 * and a hidden tab finishes a ramp a few frames after it comes back. Cannot stall a ramp: every
 * requested frame still moves it forward.
 */
// was: export const AO_FADE_MAX_STEP_MS = 34;
export const AO_FADE_MAX_STEP_MS = 50;

export interface AmbientOcclusionFrameInput {
  /** Everything but camera motion, which this decision derives from `moved` and time. */
  gate: Omit<AmbientOcclusionGateState, 'cameraMoving'>;
  /** CameraMotionTracker.update() for this frame. */
  moved: boolean;
  /** Frame time in ms (performance.now()). */
  now: number;
  /** Time of the last detected motion, as returned by the previous decision; null if none yet. */
  lastMotionAt: number | null;
  /** Blend intensity applied on the previous frame (0..1), as returned by the previous decision; 0 before the first frame. */
  intensity: number;
  /** `now` of the previous decision, as returned by it; null before the first frame. */
  lastFrameAt: number | null;
  /** false = legacy hard on/off switching (localStorage 'c4d.ambientOcclusionFade' = 'off'). */
  fade: boolean;
  /**
   * The pass's cached AO texture was computed for the current still camera (as returned by the
   * previous decision; false before the first frame). Reset by any motion.
   */
  aoFresh: boolean;
}

export interface AmbientOcclusionFrameDecision {
  /** Run the AO pass this frame (pass.enabled). */
  applyAo: boolean;
  /**
   * Compute AO (GTAO + denoise) this frame; false = the pass re-blends its cached AO texture
   * (pass.reuseAo = !computeAo). Only meaningful when applyAo is true.
   */
  computeAo: boolean;
  /** Blend intensity for this frame (pass.blendIntensity, 0..1); carry into the next frame's input. */
  intensity: number;
  /** Render another frame: the camera is settling or the intensity is still ramping. */
  requestRender: boolean;
  /** Carry into the next frame's input. */
  lastMotionAt: number | null;
  /** = input.now; carry into the next frame's input. */
  lastFrameAt: number;
  /** Carry into the next frame's input: true after a frame that computed AO, false after motion or a hard off. */
  aoFresh: boolean;
}

// was: the hard on/off decision (applyAo / requestRender / lastMotionAt only):
// export function decideAmbientOcclusionFrame(input: AmbientOcclusionFrameInput): AmbientOcclusionFrameDecision {
//   const lastMotionAt = input.moved ? input.now : input.lastMotionAt;
//   const settling = lastMotionAt !== null && input.now - lastMotionAt < AO_SETTLE_MS;
//   const appliesWhenStill = shouldApplyAmbientOcclusion({ ...input.gate, cameraMoving: false });
//   return {
//     applyAo: appliesWhenStill && !settling,
//     requestRender: appliesWhenStill && settling,
//     lastMotionAt,
//   };
// }

/**
 * Per-frame AO decision. The camera counts as moving until AO_SETTLE_MS have
 * passed since the last frame on which it moved. Rules, in order:
 *
 * 1. Hard off when AO would not apply to a still camera (disabled, see-through structure,
 *    FEA view mode, drag mode): intensity 0, pass off, no
 *    render requested — those recolour or restructure the scene and cut at once.
 * 2. Legacy (`fade` false): intensity 0 while settling, 1 once settled; the pass runs
 *    only when settled; frames are requested through the settle window so AO appears
 *    once it ends even if nothing else renders.
 * 3. Fade (`fade` true): the intensity ramps towards 0 while settling (over
 *    AO_FADE_OUT_MS) and towards 1 once settled (over AO_FADE_IN_MS), by elapsed time;
 *    the pass runs on every frame whose intensity is above 0 and is off only at exactly 0;
 *    frames are requested while settling or while the ramp has not reached its target. The
 *    clamp to exactly 0 and 1 is what ends a ramp, so a still camera at 1 requests
 *    nothing and the idle render gate behaves exactly as before. AO is COMPUTED only on
 *    the first frame after the settle window and on every rendered frame at 1 (including
 *    the clamp frame: two computations per stop, the second being the only correction for a
 *    scene change during the fade-in); the fade-out and the rest of the fade-in re-blend the
 *    pass's cached texture (`computeAo` false), so the fade-out shows the last still AO
 *    drifting for ≤ AO_FADE_OUT_MS instead of costing a full pass per frame.
 *
 * The carried intensity is clamped to [0, 1] (non-finite → 0) and a `lastFrameAt` later
 * than `now` counts as no elapsed time, so bad carried state can never jump or spin. One
 * frame advances a ramp by at most AO_FADE_MAX_STEP_MS: the idle render gate renders nothing
 * while the camera rests, so the first motion frame is typically hundreds of ms after the
 * last decision and must still start a visible fade-out rather than complete it.
 */
export function decideAmbientOcclusionFrame(input: AmbientOcclusionFrameInput): AmbientOcclusionFrameDecision {
  const lastMotionAt = input.moved ? input.now : input.lastMotionAt;
  const settling = lastMotionAt !== null && input.now - lastMotionAt < AO_SETTLE_MS;
  const appliesWhenStill = shouldApplyAmbientOcclusion({ ...input.gate, cameraMoving: false });
  const lastFrameAt = input.now;
  if (!appliesWhenStill) {
    // was: return { applyAo: false, intensity: 0, requestRender: false, lastMotionAt, lastFrameAt };
    return { applyAo: false, computeAo: false, intensity: 0, requestRender: false, lastMotionAt, lastFrameAt, aoFresh: false };
  }
  if (!input.fade) {
    // was: return { applyAo: !settling, intensity: settling ? 0 : 1, requestRender: settling, lastMotionAt, lastFrameAt };
    return { applyAo: !settling, computeAo: !settling, intensity: settling ? 0 : 1, requestRender: settling, lastMotionAt, lastFrameAt, aoFresh: !settling };
  }
  const carried = Number.isFinite(input.intensity) ? Math.min(1, Math.max(0, input.intensity)) : 0;
  // was: const dt = input.lastFrameAt === null ? 0 : Math.max(0, input.now - input.lastFrameAt);
  const dt = input.lastFrameAt === null ? 0 : Math.min(AO_FADE_MAX_STEP_MS, Math.max(0, input.now - input.lastFrameAt));
  const target = settling ? 0 : 1;
  const intensity = target < carried
    ? Math.max(0, carried - dt / AO_FADE_OUT_MS)
    : target > carried
      ? Math.min(1, carried + dt / AO_FADE_IN_MS)
      : carried;
  // was: applyAo: intensity > 0 (inline in the returned object)
  const applyAo = intensity > 0;
  // AO is computed only for a still camera: on the first frame after the settle window (the cache
  // is stale after motion) and on every rendered frame once the ramp has reached 1 (a frame then
  // means something changed, possibly the depth). The fade-out and the rest of the fade-in re-blend
  // the cached texture: the pass costs up to ~90 ms per frame in cluttered interior views, so a ramp
  // of several computed frames was the lag the fade was meant to remove.
  const fresh = input.moved ? false : input.aoFresh === true;
  const computeAo = applyAo && !settling && (!fresh || intensity === 1);
  return {
    applyAo,
    computeAo,
    intensity,
    requestRender: settling || intensity !== target,
    lastMotionAt,
    lastFrameAt,
    aoFresh: computeAo || fresh,
  };
}

/**
 * Relative tolerance for camera matrix elements: |a - b| <= tol * max(1, |a|, |b|).
 *
 * OrbitControls.update() runs every rendered frame, and with a Z-up camera its
 * spherical round trip is not bit-exact: matrixWorld drifts by about 1 ULP
 * (~2e-15 relative) on idle frames, and by at most ~3e-12 against a fixed pose
 * over thousands of frames. 1e-7 leaves several orders of magnitude of margin
 * above that, while every visible change still counts as motion: the thresholds
 * are a 0.5 mm move for a camera 5 km from the origin, a 1e-7 rad rotation, and a
 * 1e-7 relative zoom/aspect change.
 */
export const CAMERA_MOTION_TOLERANCE = 1e-7;

function matricesMatch(a: THREE.Matrix4, b: THREE.Matrix4): boolean {
  const ae = a.elements;
  const be = b.elements;
  for (let i = 0; i < 16; i++) {
    const x = ae[i];
    const y = be[i];
    if (Math.abs(x - y) > CAMERA_MOTION_TOLERANCE * Math.max(1, Math.abs(x), Math.abs(y))) {
      return false;
    }
  }
  return true;
}

/**
 * Detects camera motion between frames by comparing the camera's world and
 * projection matrices with CAMERA_MOTION_TOLERANCE. The reference pose is only
 * replaced when motion is detected, so a slow move below the tolerance per frame
 * still adds up until it counts.
 */
export class CameraMotionTracker {
  private lastMatrixWorld: THREE.Matrix4 | null = null;
  private lastProjectionMatrix: THREE.Matrix4 | null = null;

  /** true when camera.matrixWorld or projectionMatrix moved beyond the tolerance since the last detected motion (first call → true). */
  update(camera: THREE.Camera): boolean {
    if (this.lastMatrixWorld === null || this.lastProjectionMatrix === null) {
      this.lastMatrixWorld = camera.matrixWorld.clone();
      this.lastProjectionMatrix = camera.projectionMatrix.clone();
      return true;
    }
    const moved = !matricesMatch(this.lastMatrixWorld, camera.matrixWorld)
      || !matricesMatch(this.lastProjectionMatrix, camera.projectionMatrix);
    if (moved) {
      this.lastMatrixWorld.copy(camera.matrixWorld);
      this.lastProjectionMatrix.copy(camera.projectionMatrix);
    }
    return moved;
  }

  reset(): void {
    this.lastMatrixWorld = null;
    this.lastProjectionMatrix = null;
  }
}
